import { Grid, Radio } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { PACKAGE_TYPE, PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { useIntl } from 'react-intl';
import { capacitorService } from '../../../../application/CapacitorService';
import { revenueCatService } from '../../../../application/RevenueCatService';
import { EventTrackTypes } from '../../../../constants/EventTracks';
import { useCaptureEvents } from '../../../../hooks/useCaptureEvents';
import Button from '../../../../react-components/Button';
import { Text } from '../../../../react-components/Text';
import { useAppContext } from '../../context/appContext/context';
import { useRetrieveRevenueCatCustomer } from '../../hooks/useRetrieveRevenueCatCustomer';
import { useSubscribeToPackage } from '../../hooks/useSubscribeToPackage';
import { UpgradeSubscriptionSlide } from './UpgradeSubscriptionSlide';

enum AndroidProducts {
  DRIVERS_LITE_MONTHLY = `drivers_lite:drivers-lite-monthly`,
  DRIVERS_LITE_YEARLY = `drivers_lite:drivers-lite-yearly`,
}

enum IOSProducts {
  DRIVERS_LITE_MONTHLY = `lite_monthly`,
  DRIVERS_LITE_YEARLY = `lite_yearly`,
}

interface UpgradeSubscriptionNativeDialogProps {
  onClose: () => void;
}

export const UpgradeSubscriptionNativeDialog = ({ onClose }: UpgradeSubscriptionNativeDialogProps) => {
  const intl = useIntl();
  const [packages, setPackages] = useState<PurchasesPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PurchasesPackage | null>(null);
  const { capture } = useCaptureEvents();
  const { appState } = useAppContext();
  const { run: subscribeToPackage, success } = useSubscribeToPackage();
  const { forceRefetch: refetchCustomerInfo } = useRetrieveRevenueCatCustomer();

  useEffect(() => {
    if ((appState.revenueCatCustomer?.activeSubscriptions ?? []).length > 0) {
      onClose();

      // RevenueCat has a cache of 5min.
      // If user has cancelled his subscription but in cache remains the active subscription, force here a refetch.
      refetchCustomerInfo();

      return;
    }

    (async () => {
      setLoading(true);

      try {
        const subscriptionPackages = await revenueCatService.getSubscriptionPackages();

        let monthlyPackage: PurchasesPackage | undefined;
        let yearlyPackage: PurchasesPackage | undefined;

        if (capacitorService.isAndroid()) {
          monthlyPackage = subscriptionPackages.find(
            (p) => p.product.identifier === AndroidProducts.DRIVERS_LITE_MONTHLY
          );
          yearlyPackage = subscriptionPackages.find(
            (p) => p.product.identifier === AndroidProducts.DRIVERS_LITE_YEARLY
          );
        }

        if (capacitorService.isIOS()) {
          monthlyPackage = subscriptionPackages.find((p) => p.product.identifier === IOSProducts.DRIVERS_LITE_MONTHLY);
          yearlyPackage = subscriptionPackages.find((p) => p.product.identifier === IOSProducts.DRIVERS_LITE_YEARLY);
        }
        if (!monthlyPackage || !yearlyPackage) {
          onClose();
          return;
        }

        setSelectedProduct(monthlyPackage!);

        setPackages([monthlyPackage!, yearlyPackage!]);

        setLoading(false);
      } catch (error) {
        console.error(`Error getting subscription packages`, error);
        onClose();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      onClose();
    }
  }, [success, onClose]);

  console.info(`packages`, packages);

  return (
    <UpgradeSubscriptionSlide
      children={
        <>
          <Grid container direction="column" gap="12px" flex="1">
            {packages.map((p) => {
              const isAnnual = p.packageType === PACKAGE_TYPE.ANNUAL;
              const isSelected = selectedProduct?.product.identifier === p.product.identifier;

              console.info(`p.product.identifier`, p.product.identifier);
              console.info(`isSelected`, isSelected);

              const planLabel = isAnnual
                ? intl.formatMessage({ id: `drivers.subscription_upgrade_dialog.plan_type.annual` })
                : intl.formatMessage({ id: `drivers.subscription_upgrade_dialog.plan_type.monthly` });

              const planCost = isAnnual
                ? intl.formatMessage(
                    { id: `drivers.subscription_upgrade_dialog.plan_cost.annual` },
                    {
                      value: p.product.pricePerYearString,
                    }
                  )
                : intl.formatMessage(
                    { id: `drivers.subscription_upgrade_dialog.plan_cost.monthly` },
                    {
                      value: p.product.pricePerMonthString,
                    }
                  );

              return (
                <Button
                  id={isAnnual ? `annual_payment` : `monthly_payment`}
                  variant="flat"
                  style={{
                    display: `flex`,
                    flexDirection: `row`,
                    justifyContent: `flex-start`,
                    alignItems: `flex-start`,
                    gap: `8px`,
                    cursor: `pointer`,
                    padding: `10px`,
                    border: isSelected
                      ? `1px solid ${RoutalPalette.primary40}`
                      : `1px solid ${RoutalPalette.neutral10}`,
                    borderRadius: `10px`,
                    margin: 0,
                  }}
                  onClick={() => {
                    setSelectedProduct(p);
                  }}
                >
                  <Radio checked={isSelected} />

                  <Grid container direction="column" gap="4px" alignItems="flex-start">
                    <Text variant="body-large" style={{ cursor: `pointer` }} isBold>
                      {planLabel}
                    </Text>

                    <Text variant="body-large" style={{ cursor: `pointer`, color: RoutalPalette.secondary00 }}>
                      {planCost}
                    </Text>
                  </Grid>

                  {/* TODO: OFFER DISCOUNT IF ANNUAL */}
                </Button>
              );
            })}
          </Grid>

          <Text
            variant="label"
            style={{
              textAlign: `center`,
            }}
            intlId="drivers.subscription_upgrade_dialog.terms_and_conditions"
            intlValues={{
              a: (chunks: any) => (
                <Text
                  tagName="span"
                  variant="label"
                  onClick={() => {
                    window.open(`https://www.routal.com/en/aviso-legal`, `_blank`);
                  }}
                  style={{ textDecoration: `underline`, color: RoutalPalette.primary20, fontWeight: 500 }}
                >
                  {chunks}
                </Text>
              ),
            }}
          />

          <VSpacer small />

          <Button
            intlId="drivers.subscription_upgrade_dialog.subscribe"
            disabled={!selectedProduct}
            onClick={() => {
              capture({
                type: EventTrackTypes.Subscribe,
                payload: {
                  [`Action`]: `Subscribe`,
                },
              });
              subscribeToPackage(selectedProduct!);
            }}
          />
        </>
      }
      onClose={onClose}
      openSlide={packages.length > 0 && !loading}
      titleId={`drivers.subscription_upgrade_dialog.title2`}
      subtitleId={`drivers.subscription_upgrade_dialog.subtitle`}
    />
  );
};
